/**
 * @file SearchableDropdown.js
 * @version 1.13.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-10
 * @modified 2026-11-02
 * @description Searchable dropdown with filtering capabilities
 *
 *              `badge` is shown only when it has content (hasBadge) — a blank
 *              or null badge renders nothing and holds no space.
 *
 *              An option marked `archived: true` is hidden until the viewer
 *              asks for it. See the archived block below.
 *
 *              `tag` draws a short pill after the label (e.g. 'STOCK'), and
 *              `pinned: true` lifts an option to the top of the list, below
 *              any blank "Select…"/"Any" row. Both are used for the stock
 *              supplier — see utils/stockSupplier.js.
 *
 *              1.10.0 (2026-10-12): `openRequest` and `redirectOpen`, so a
 *              group of dropdowns can send the user to one of them first
 *              (TransportStack: Driver before Haulier and Vehicle). Bumping
 *              `openRequest` opens this menu, as a click would.
 *              `redirectOpen`, when set, is called INSTEAD of opening — on a
 *              click or an opening key (Enter, Space, ↑/↓, typing) — and Tab
 *              and ←/→ still move on, so a keyboard is never trapped.
 *
 *              1.11.0 (2026-10-12): an option marked `sticky: true` is pinned
 *              first, listed whatever is typed in the search, and stays in
 *              view at the top of the list while it scrolls (the STOCK
 *              choice of a self-billed PO's delivery / storage address).
 *              `placeholderClassName` styles the trigger's text while nothing
 *              is chosen (e.g. 'sd-placeholder-error' for a required pick).
 *
 *              1.12.0 (2026-10-14): a list searched ON THE SERVER — for a
 *              picker over more rows than it can load (a supplier's thousands
 *              of bills, CreditBillPicker). `onSearchChange(term)` hears every
 *              change to the search box (the caller debounces and fetches);
 *              `serverFiltered` shows `options` as given (the caller's
 *              matches — no filtering here; pinned / sticky still lead);
 *              `listNote` is a line under the list ("Showing the newest 50 of
 *              13,007 — type to narrow"); `loading` says "Searching…" instead
 *              of "No results found"; `searchHelp` sits beside the box (the
 *              SearchHelp "?").
 *
 *              1.13.0 (2026-11-02): several `sticky` options stay in view
 *              TOGETHER — they are drawn in one sticky band at the top of the
 *              list (each sticky alone at top: 0 would land on the others).
 *              The item's supplier, or its supplier group's depots, pinned in
 *              the supplier pickers (utils/itemSupplier). `listTag` is a tag
 *              drawn in the list only — never in the trigger once chosen (a
 *              long "Item's supplier" there squeezed a narrow line cell).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableDropdown.css';
import useDropdownPosition from '../hooks/useDropdownPosition';
import useDropdownKeys from '../hooks/useDropdownKeys';
import useRepMobile from '../hooks/useRepMobile';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';

/**
 * A badge (account number, item code, additive code) is drawn only when there
 * is something in it. A blank one used to be drawn invisible — opacity 0, but
 * still 55px wide — so that names lined up; Cam's call is that a contact or
 * item with no number should simply start with its name, not with a gap.
 */
const hasBadge = (badge) => badge !== undefined && badge !== null && String(badge).trim() !== '';

function SearchableDropdown({ options, value, onChange, placeholder = "Select...", searchPlaceholder = "Search...", compact = false, disabled = false, renderActions = null, autoOpen = false,
  // Where the menu must fit, and whether it may leave its scroll container to
  // get there. Needed by dropdowns inside short overflow boxes — see
  // useDropdownPosition.
  boundarySelector = null, escapeOverflow = false,
  // A number; each change opens the menu (see the header).
  openRequest = 0,
  // () => void, or null; called instead of opening (see the header).
  redirectOpen = null,
  // A class for the trigger text while nothing is chosen (see the header).
  placeholderClassName = '',
  // A list searched on the server (see the header, 1.12.0).
  onSearchChange = null, serverFiltered = false, listNote = null, loading = false, searchHelp = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  /*
   * ARCHIVED OPTIONS.
   *
   * An item picker draws on the whole catalogue, and most of that catalogue is
   * retired — seasonal blends are archived the moment their season ends. Listed
   * plainly they bury the item somebody is actually looking for, and are easy
   * to pick by accident because an archived blend's name reads exactly like a
   * live one. Dropped entirely they cannot be picked at all, which is wrong for
   * the minority of cases that legitimately need one.
   *
   * So: out of the way by default, one toggle away, and unmistakable once
   * shown. Handled here rather than at each call site because "any item picker"
   * means this one component — a rule applied at each of a dozen pickers is a
   * rule the thirteenth will not have.
   *
   * Not reset when the menu closes: turning it on is a deliberate act, and
   * re-hiding between two picks in the same job would just be re-done.
   */
  const [showArchived, setShowArchived] = useState(false);
  const hasArchived = (options || []).some(opt => opt && opt.archived);
  const toggleId = useRef(`sd-archived-${Math.random().toString(36).slice(2, 9)}`).current;
  // The keyboard cursor — NOT the selected value, which `.active` marks. See
  // hooks/useDropdownKeys.
  const [cursor, setCursor] = useState(0);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef(null);
  // The phone shell turns this menu into a near-full-screen sheet, which needs
  // two things CSS cannot express. See the same pair in MultiSelectDropdown.
  const repMobile = useRepMobile();
  const [scrollRef, scroll] = useScrollAffordance([isOpen, searchTerm, options?.length]);
  // One element, two refs — the cursor scrolls itself into view through
  // optionsRef, and the affordance measures the same box.
  const setOptionsEl = useCallback((el) => {
    optionsRef.current = el;
    scrollRef.current = el;
  }, [scrollRef]);
  const { flipUp, theHeight, menuStyle } = useDropdownPosition(dropdownRef, isOpen, { boundarySelector, escapeOverflow });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /*
   * Focus the search field on open — everywhere but the phone.
   *
   * On a phone that throws the keyboard up over the options before the list has
   * been looked at, and turns the tap that opened the menu into a commitment to
   * typing. There, opening the menu opens the menu; the field waits to be
   * tapped. autoOpen still focuses, because a caller asking for the menu to be
   * open on mount is asking for a search box to type into.
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current && (!repMobile || autoOpen)) {
      searchInputRef.current.focus();
    }
  }, [isOpen, repMobile, autoOpen]);

  // Asked to open by whoever holds this dropdown (openRequest bumped).
  useEffect(() => {
    if (!openRequest || disabled) return;
    setSearchTerm('');
    setCursor(Math.max(0, options.findIndex(o => o.value === value)));
    setIsOpen(true);
    // Only the request opens it; the rest is read as it is at the time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  // Opt-in: open (and focus the search input) on mount.
  useEffect(() => {
    if (autoOpen && !disabled) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On open, scroll the currently-selected option into view (centred) and let
  // its .active highlight show which one is the current selection.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => {
      const box = optionsRef.current;
      const active = box && box.querySelector('.dropdown-item.active');
      if (!box || !active) return;
      const a = active.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      box.scrollTop += (a.top - b.top) - (b.height - a.height) / 2;
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // The caller searching on the server hears every change to the box —
  // through a ref, so a new callback each render never re-fires it.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  useEffect(() => {
    if (onSearchChangeRef.current) onSearchChangeRef.current(searchTerm);
  }, [searchTerm]);

  const selectedOption = options.find(opt => opt.value === value);

  const matchingOptions = options.filter(option => {
    /*
     * The SELECTED option is listed whatever its state. A standing order that
     * already names an archived blend has to keep naming it — hiding the
     * current value would show an empty trigger and quietly drop the product
     * on the next save.
     */
    if (option.archived && !showArchived && option.value !== value) return false;
    // A sticky option is always offered, whatever is typed (see the header).
    if (option.sticky) return true;
    // Already the server's matches for what is typed.
    if (serverFiltered) return true;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    // Use searchText if provided, otherwise fall back to label and value.
    // Guard for nullish label / searchText (a caller might render a
    // pure-JSX renderLabel without a string label), and for nullish value
    // — null is a legitimate sentinel for "Any / All / None" options, and
    // calling .toString() on it would throw the moment the user typed.
    const textToSearch = option.searchText || option.label || '';
    const valueText = option.value == null ? '' : String(option.value);
    if (textToSearch.toLowerCase().includes(search) ||
        valueText.toLowerCase().includes(search)) {
      return true;
    }
    // Phone/number search: compare digits-only so formatting and spaces don't
    // block a match (e.g. "01234 567890" finds a stored "+44 1234 567890").
    // Gated on 3+ digits so short text queries don't broaden unexpectedly.
    const searchDigits = searchTerm.replace(/\D/g, '');
    if (searchDigits.length >= 3) {
      return textToSearch.replace(/\D/g, '').includes(searchDigits);
    }
    return false;
  });

  // Pinned options first — after the blank "Select…"/"Any" row, which stays
  // at the very top. A stable partition, so everything else keeps its order.
  const isBlankRow = (o) => o.value === '' || o.value == null;
  const isPinned = (o) => o.pinned || o.sticky;
  const filteredOptions = options.some(isPinned)
    ? [
        ...matchingOptions.filter(isBlankRow),
        ...matchingOptions.filter(o => o.sticky && !isBlankRow(o)),
        ...matchingOptions.filter(o => o.pinned && !o.sticky && !isBlankRow(o)),
        ...matchingOptions.filter(o => !isPinned(o) && !isBlankRow(o))
      ]
    : matchingOptions;

  // A new search is a new list: start the cursor back at the top of it.
  useEffect(() => { setCursor(0); }, [searchTerm]);

  // Closing by keyboard (Escape, Tab) does not pass through handleToggle, so
  // clear the search here — otherwise ↓ reopens onto the last filtered list.
  useEffect(() => { if (!isOpen) setSearchTerm(''); }, [isOpen]);

  const indexOfValue = () => filteredOptions.findIndex(o => o.value === value);

  // Closed keys on the trigger; open keys on the search box, which takes focus
  // the moment the menu opens.
  const { onTriggerKeyDown, onMenuKeyDown } = useDropdownKeys({
    isOpen,
    setIsOpen,
    navigable: filteredOptions,
    cursor,
    setCursor,
    onChoose: (option) => {
      onChange(option.value);
      setSearchTerm('');
    },
    triggerRef,
    listRef: optionsRef,
    // Typing on a closed trigger starts the search with that character, rather
    // than opening an empty box and losing the keystroke.
    onType: (ch) => setSearchTerm(ch),
    indexOfValue,
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
      setCursor(Math.max(0, options.findIndex(o => o.value === value)));
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`searchable-dropdown ${compact ? 'compact' : ''} ${flipUp ? 'flip-up' : ''}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        className="dropdown-trigger"
        onClick={disabled ? undefined : (redirectOpen && !isOpen ? () => redirectOpen() : handleToggle)}
        onKeyDown={disabled ? undefined : (redirectOpen && !isOpen
          ? (e) => {
            const { key } = e;
            const opens = key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp'
              || (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey);
            if (!opens) { onTriggerKeyDown(e); return; }
            e.preventDefault();
            redirectOpen();
          }
          : onTriggerKeyDown)}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`dropdown-trigger-text${!selectedOption && placeholderClassName ? ` ${placeholderClassName}` : ''}`}>
          {selectedOption ? (
            <>
              {hasBadge(selectedOption.badge) && <span className="dropdown-item-badge">{selectedOption.badge}</span>}
              {selectedOption.renderLabel || selectedOption.label}
              {selectedOption.tag && <span className="sd-tag">{selectedOption.tag}</span>}
              {selectedOption.archived && <span className="archived-badge sd-archived-badge">Archived</span>}
            </>
          ) : placeholder}
        </span>
        <span className="material-symbols-rounded">{isOpen ? 'expand_less' : 'expand_more'}</span>
      </button>

      {isOpen && (
        <div className="dropdown-menu"
             style={{ ...(theHeight ? { height: `${theHeight}px` } : null), ...(menuStyle || null) }}>
          {/* The way out.

              The sheet used to leave a strip of page showing at the top to
              tap on; at 20px from the top there is no strip left, so the
              dismiss has to be part of the sheet. Full width and transparent
              so it reads as the head of the sheet rather than a control — the
              icon says what it does, and the whole bar takes the tap, which
              is the biggest target on the screen that isn't an option. */}
          {repMobile && (
            <button
              type="button"
              className="dropdown-sheet-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          )}
          <div className="dropdown-search">
            <span className="material-symbols-rounded search-icon">search</span>
            <input
              ref={searchInputRef}
              type="text"
              className="dropdown-search-input"
              // A search box, not a form field: the browser's saved-values
              // popup has nothing useful to offer and sits over the list.
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              name="dropdown-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onMenuKeyDown}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
                type="button"
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
            {searchHelp}
          </div>
          {hasArchived && (
            <div className="dropdown-archived-toggle">
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id={toggleId}
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label htmlFor={toggleId}></label>
                </div>
              </div>
              <label htmlFor={toggleId} className="dropdown-archived-label">Show archived</label>
            </div>
          )}
          <OptionsBox repMobile={repMobile} setOptionsEl={setOptionsEl} optionsRef={optionsRef} scroll={scroll}>
            {filteredOptions.length === 0 ? (
              <div className="dropdown-item no-results">
                {loading ? 'Searching…' : 'No results found'}
              </div>
            ) : (
              withStickyBand(filteredOptions, (option, index) => (
                <div
                  key={option.value}
                  className={`dropdown-item ${value === option.value ? 'active' : ''} ${cursor === index ? 'is-cursor' : ''} ${renderActions ? 'has-actions' : ''} ${option.archived ? 'is-archived' : ''} ${option.sticky ? 'is-sticky' : ''}`.trim()}
                  data-cursor={cursor === index ? 'true' : undefined}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => handleSelect(option.value)}
                >
                  <div className="dropdown-item-content">
                    {hasBadge(option.badge) && <span className="dropdown-item-badge">{option.badge}</span>}
                    <span className="dropdown-item-label">{option.renderLabel || option.label}</span>
                    {option.tag && <span className="sd-tag">{option.tag}</span>}
                    {option.listTag && <span className="sd-tag sd-list-tag">{option.listTag}</span>}
                    {option.archived && <span className="archived-badge sd-archived-badge">Archived</span>}
                  </div>
                  {renderActions && (
                    <div className="dropdown-item-actions" onClick={(e) => e.stopPropagation()}>
                      {renderActions(option)}
                    </div>
                  )}
                </div>
              ))
            )}
          </OptionsBox>
          {listNote && <div className="dropdown-list-note" role="status">{listNote}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * The options drawn, the sticky ones (contiguous: the list puts them first,
 * after any blank row) in ONE band that stays at the top while the rest
 * scrolls — so several stay in view together, none over another (1.13.0).
 */
function withStickyBand(options, renderOption) {
  const first = options.findIndex(o => o.sticky);
  if (first < 0) return options.map(renderOption);
  let end = first;
  while (end < options.length && options[end].sticky) end += 1;
  return [
    ...options.slice(0, first).map((o, i) => renderOption(o, i)),
    <div className="dropdown-sticky-band" key="__sticky-band">
      {options.slice(first, end).map((o, i) => renderOption(o, first + i))}
    </div>,
    ...options.slice(end).map((o, i) => renderOption(o, end + i))
  ];
}

/**
 * The scrolling option list, with "there is more this way" cues on the phone.
 *
 * The sheet fills the screen, so there is no edge of a menu to tell you the
 * list carries on past it — and with the keyboard up there is very little of it
 * showing. The cue's wrapper has to sit OUTSIDE the scrolling box or it travels
 * with the content; see ScrollMore.css. Phone only, so every existing caller's
 * markup is exactly what it was.
 */
function OptionsBox({ repMobile, setOptionsEl, optionsRef, scroll, children }) {
  if (!repMobile) {
    return <div className="dropdown-options" ref={setOptionsEl}>{children}</div>;
  }
  return (
    <div className="scroll-more-wrap">
      <div className="dropdown-options scroll-more-box" ref={setOptionsEl}>{children}</div>
      {/* `remaining` is the proportion still unscrolled the cue's own way, so
          the up cue is handed its mirror and is drawn only once there is
          something above to go back to. */}
      <ScrollMore
        scrollRef={optionsRef}
        direction="up"
        remaining={scroll.above ? 1 - scroll.remaining : 0}
      />
      <ScrollMore scrollRef={optionsRef} remaining={scroll.remaining} />
    </div>
  );
}

export default SearchableDropdown;
